/**
 * JSON 相关通用工具。
 *
 * 与项目内常见的「服务端字段可能是 JSON 字符串 / 已解析对象 / 数组」的场景对齐：
 * 调用方无需提前判别入参形态，直接传给本工具即可拿到结构化结果。
 *
 * @module utils/json
 */

import { isArray, isNil, isPlainObject, isString } from 'lodash-es';

const LOG_PREFIX = '[utils/json]';

/**
 * 将 JSON 字符串解析为对象 / 数组；已是结构化数据或非字符串则原样返回。
 *
 * 设计要点：
 * - 空值（`null` / `undefined` / 空字符串）原样返回，便于调用方按需做默认值处理；
 * - 已是 plain object 或数组：原样返回（不做深克隆，避免无谓开销）；
 * - 非字符串、非空、非对象/数组（如 number / boolean）：原样返回；
 * - 字符串：尝试 `JSON.parse`，失败时打印一次 warn 并返回原字符串，让调用方按降级路径处理。
 *
 * @param {unknown} value 待解析值
 * @param {{ warnLabel?: string }} [options]
 * @param {string} [options.warnLabel='field'] 解析失败 warn 中的字段标签，便于定位上下文
 * @returns {unknown} 解析后的值；解析失败时返回原字符串
 *
 * @example
 * parseJsonValue('{"a":1}');           // { a: 1 }
 * parseJsonValue({ a: 1 });            // { a: 1 }（原样返回）
 * parseJsonValue('');                  // ''（原样返回）
 * parseJsonValue('not json', { warnLabel: 'stockDetail' }); // 'not json' + console.warn
 */
export function parseJsonValue(value, options = {}) {
  const { warnLabel = 'field' } = options;
  if (isNil(value) || value === '') {
    return value;
  }
  if (isPlainObject(value) || isArray(value)) {
    return value;
  }
  if (!isString(value)) {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch (err) {
    console.warn(`${LOG_PREFIX} ${warnLabel} JSON parse failed`, err);
    return value;
  }
}
